import { useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import imageCompression from 'browser-image-compression';

export const useImageMessage = (currentUserId: string, otherUserId: string, onImageSent: () => void, replyToMessageId?: number | null) => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);

  const handleImageSelect = async (file: File) => {
    setCompressing(true);
    try {
      const options = {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
        fileType: 'image/jpeg'
      };
      
      const compressedFile = await imageCompression(file, options);
      console.log(`Original: ${(file.size / 1024 / 1024).toFixed(2)}MB, Compressed: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
      
      setSelectedImage(compressedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(compressedFile);
    } catch (error) {
      console.error('Image compression failed:', error);
      alert('Failed to process image');
    } finally {
      setCompressing(false);
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('RecipientUserId', otherUserId);
      formData.append('File', selectedImage);

      await axiosInstance.post('/api/v2/messages/image', formData);

      setSelectedImage(null);
      setImagePreview(null);
      onImageSent();
      console.log('Image uploaded successfully');
    } catch (error: any) {
      console.error('Image upload failed:', error);
      console.error('Error response:', error.response?.data);
      alert(`Failed to upload image: ${error.response?.data?.message || error.response?.data?.error || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const cancelImageUpload = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  return {
    selectedImage,
    imagePreview,
    uploading,
    compressing,
    handleImageSelect,
    uploadImage,
    cancelImageUpload
  };
};
